import React, { useEffect, useState, useRef, useMemo } from "react";
import ProductInfo from "../../components/pageProps/productDetails/ProductInfo";
import { FaSearchPlus } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSingleProduct } from "../../redux/single-productSlice";
import Image from "../../components/designLayouts/Image";
import { placeholder } from "../../assets/images";
import processSrc from "../../utils/processSrc";
import { selectFilters, fetchFilters } from "../../redux/filtersSlice";

const ProductDetails = () => {
  const dispatch = useDispatch();
  const filters = useSelector(selectFilters);
  const { filters: filterData, status } = filters;
  const { ratio: aspect_ratio = "1/1" } = filterData || {};

  const { _id } = useParams();
  const {
    data: productInfo,
    isLoading,
    error,
  } = useSelector((state) => state.singleproduct);

  const info = productInfo?.Result?.[0];
  const [selectedImage, setSelectedImage] = useState("");
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const zoomRef = useRef();
  const [mainImageHeight, setMainImageHeight] = useState(0);

  const scrollRef = useRef();

  const onWheel = (e) => {
    if (e.deltaY === 0) return;
    e.preventDefault();
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft + e.deltaY,
      behavior: "smooth",
    });
  };
  const ref = scrollRef.current;
  const handleScreenResize = () => {
    const isSmallScreen = window.matchMedia("(max-width: 1279px)").matches;

    // Add the event listener for small screens
    if (isSmallScreen && ref) {
      ref.addEventListener("wheel", onWheel, { passive: false });
    }
    // Remove the event listener for larger screens
    else if (ref) {
      ref.removeEventListener("wheel", onWheel);
    }
  };

  // Initial setup
  handleScreenResize();

  // Re-evaluate on window resize
  window.addEventListener("resize", handleScreenResize);

  useEffect(() => {
    // Fetch filters if they haven't been fetched already
    if (status === "idle") {
      dispatch(fetchFilters());
    }
  }, [dispatch, status]);

  useEffect(() => {
    if (_id) {
      dispatch(fetchSingleProduct({ product_id: _id }));
    }
  }, [dispatch, _id]);

  useEffect(() => {
    if (info?.images?.length) {
      setSelectedImage(processSrc(info.images[0]?.path || placeholder));
    }
  }, [info]);

  useEffect(() => {
    const updateHeightAndZoom = () => {
      const isXLScreen = window.matchMedia("(min-width: 1280px)").matches;

      if (zoomRef.current) {
        if (isXLScreen) {
          setMainImageHeight(zoomRef.current.offsetHeight);
        } else {
          setMainImageHeight(0);
        }
      }
    };

    updateHeightAndZoom(); // Initial calculation
    window.addEventListener("resize", updateHeightAndZoom);

    return () => window.removeEventListener("resize", updateHeightAndZoom);
  }, [info]);

  const handleMouseMove = (e) => {
    if (selectedImage === placeholder || !zoomRef.current) return;

    const rect = zoomRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setZoomPosition({
      x: Math.min(Math.max(x, 0), 100),
      y: Math.min(Math.max(y, 0), 100),
    });
  };

  const handleMouseLeave = () => {
    if (selectedImage === placeholder) return;
    setZoomPosition({ x: 0, y: 0 });
  };

  const productInfoComponent = useMemo(
    () => <ProductInfo productInfo={info} aspectRatio={aspect_ratio} />,
    [info, aspect_ratio],
  );

  if (isLoading)
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="h-52 md:h-96 aspect-square   border-4 border-t-blue-500 border-gray-300 rounded-full animate-spin"></div>
      </div>
    );

  if (error) throw new Error("Could not fetch product details");

  const shouldFadeZoomIcon = zoomPosition.x !== 0 && zoomPosition.y !== 0;

  return (
    <div className="w-full mx-auto  border-b-gray-300 ">
      <div className="mx-auto">
        <div className="flex flex-col mdl:flex-row gap-4  pb-10 p-4">
          <div className="flex gap-2 xl:flex-row flex-col-reverse justify-start items-start max-h-max">
            {/* Vertical Image Gallery */}
            {info?.images[0]?.path && (
              <div
                ref={scrollRef}
                className={`flex xl:flex-col p-1 gap-2 lgl:w-[500px] xl:w-24 cursor-grab sc overflow-x-auto xl:overflow-y-auto scrollbar-none items-start justify-start`}
                style={{
                  height: mainImageHeight > 0 ? `${mainImageHeight}px` : "auto",
                }}
                onWheel={onWheel}
              >
                {info.images.map((image, index) => (
                  <Image
                    key={index}
                    className={`w-20 xl:w-full h-fit object-cover cursor-pointer rounded-lg transition-all ${
                      selectedImage === processSrc(image?.path)
                        ? "ring-2 ring-primeColor"
                        : ""
                    }`}
                    imgSrc={image?.path || placeholder}
                    aspectRatio={aspect_ratio}
                    alt={`Thumbnail ${index + 1}`}
                    onClick={() => setSelectedImage(processSrc(image?.path))}
                  />
                ))}
              </div>
            )}

            {/* Main Image with Zoom */}
            <div className="w-full lgl:w-fit flex flex-col items-center relative">
              <div
                ref={zoomRef}
                className="relative w-full bg-gray-200 rounded-lg overflow-hidden group"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                {selectedImage !== placeholder && (
                  <div
                    className={`absolute top-4 right-6 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-200 transition-all ${
                      shouldFadeZoomIcon ? "opacity-0" : "opacity-100"
                    }`}
                    style={{ transition: "opacity 0.3s ease" }}
                  >
                    <FaSearchPlus className="text-xl text-primeColor" />
                  </div>
                )}
                <Image
                  className="w-full lgl:w-[500px] h-auto object-cover"
                  alt="Selected product"
                  aspectRatio={aspect_ratio}
                  imgSrc={selectedImage || placeholder}
                />
                <div
                  className="absolute top-0 left-0 w-full h-full pointer-events-none"
                  style={{
                    backgroundImage: `url(${selectedImage?.replace(/ /g, "%20")})`,
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "200%",
                    backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    opacity: zoomPosition.x && zoomPosition.y ? 1 : 0,
                    transition: "opacity 0.2s ease",
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div className="flex-1 gap-6 p-4">{productInfoComponent}</div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
